import { Logging } from '#common/logging';
import { withStats } from '#common/stats';
import { getFetchErrorMessage, HTTP_STATUS_FORBIDDEN, HTTP_STATUS_GONE, HTTP_STATUS_NOT_FOUND, HTTP_STATUS_OK, HTTP_STATUS_PARTIAL_CONTENT, HTTP_STATUS_RANGE_NOT_SATISFIABLE, parseChecksum, sanitizeUrl, } from '#object-store/common';
import { ObjectNotFoundError, ObjectStoreError, ObjectStoreStats, PresignedUrlError, } from '#object-store/types';
const FETCH_TIMEOUT_MS = 60000; // 60 seconds
export const createHttpStore = () => {
    return new HttpStoreImpl();
};
class HttpStoreImpl {
    log = Logging.getLogger('HttpStore');
    statistics = new ObjectStoreStats(this.log);
    async getObject(url, offset, size) {
        return withStats(this.statistics.getObject, async () => this.fetchFromUrl(url, offset, size), {
            url,
            offset,
            size,
        });
    }
    async fetchFromUrl(url, offset, size) {
        let response;
        try {
            response = await this.fetchWithRange(url, offset, size);
        }
        catch (error) {
            this.throwNetworkError(url, offset, size, error, 'HTTP request failed', 'fetching object data');
        }
        try {
            return this.processResponse(response, url, size);
        }
        catch (error) {
            if (error instanceof ObjectStoreError) {
                throw error;
            }
            this.throwNetworkError(url, offset, size, error, 'Failed to read response body', 'reading response body');
        }
    }
    async fetchWithRange(url, offset, size) {
        const lastByte = offset + size - 1;
        const range = `bytes=${offset}-${lastByte}`;
        // TODO: LUCID-24739 Optimize WebFs HttpStore's fetch to reuse pool of connections
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
        try {
            return await fetch(url, {
                method: 'GET',
                headers: { Range: range },
                signal: controller.signal,
            });
        }
        finally {
            clearTimeout(timeoutId);
        }
    }
    async processResponse(response, url, expectedSize) {
        if (!response.ok) {
            this.handleHttpError(response, url);
        }
        this.validateRangeResponse(response, url);
        const arrayBuffer = await response.arrayBuffer();
        const data = new Uint8Array(arrayBuffer);
        this.validateDataSize(data, expectedSize, url);
        const checksum = parseChecksum(response);
        return { data, checksum };
    }
    validateRangeResponse(response, url) {
        if (response.status !== HTTP_STATUS_PARTIAL_CONTENT && response.status !== HTTP_STATUS_OK) {
            const sanitizedUrl = sanitizeUrl(url);
            throw new ObjectStoreError(`Unexpected status ${response.status} for range request to URL: ${sanitizedUrl}`);
        }
    }
    validateDataSize(data, expectedSize, url) {
        if (data.length !== expectedSize) {
            const sanitizedUrl = sanitizeUrl(url);
            this.log.warn({ url: sanitizedUrl, requestedSize: expectedSize, receivedSize: data.length }, 'Received data size does not match requested size');
        }
    }
    stats() {
        return this.statistics;
    }
    throwNetworkError(url, offset, size, error, logMessage, errorContext) {
        const sanitizedUrl = sanitizeUrl(url);
        this.log.error({ url: sanitizedUrl, offset, size, error }, logMessage);
        throw new PresignedUrlError(`Network error ${errorContext} from ${sanitizedUrl}: ${getFetchErrorMessage(error)}`);
    }
    handleHttpError(response, url) {
        const statusCode = response.status;
        const sanitizedUrl = sanitizeUrl(url);
        if (statusCode === HTTP_STATUS_NOT_FOUND) {
            throw new ObjectNotFoundError(`Object not found at URL: ${sanitizedUrl}`);
        }
        if (statusCode === HTTP_STATUS_FORBIDDEN) {
            throw new ObjectStoreError(`Access denied to URL: ${sanitizedUrl}`);
        }
        if (statusCode === HTTP_STATUS_GONE) {
            throw new ObjectStoreError(`URL expired or no longer available: ${sanitizedUrl}`);
        }
        if (statusCode === HTTP_STATUS_RANGE_NOT_SATISFIABLE) {
            throw new ObjectStoreError(`Byte range not satisfiable for URL: ${sanitizedUrl}`);
        }
        throw new ObjectStoreError(`HTTP error ${statusCode} while fetching from URL: ${sanitizedUrl}`);
    }
}
